// src/pages/admin/CoachList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import { getUserListExportColumns } from '../../utils/userListExport';
import API from '../../api';
import UserLink from '../../components/common/UserLink';
import { formatFullName } from '../../utils/name';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import ListRecordIdBadge from '../../components/common/ListRecordIdBadge';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { userListSortColumns } from '../../config/tableSortColumns';
import { resolveRowId } from '../../utils/tableSort';
import ManageStudentAssignmentsModal from '../../components/coach/ManageStudentAssignments';
import UserRowActions from '../../components/common/UserRowActions';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import { getAdminLicense } from '../../api/license';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const CoachList = () => {
    const { t } = useTranslation();
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);
    const [filter, setFilter] = useState('active'); // all, active, archived
    const [manageModal, setManageModal] = useState({ isOpen: false, coachId: null, coachName: '' });
    const [coachLicenseAtLimit, setCoachLicenseAtLimit] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        getAdminLicense()
            .then((data) => {
                const max = data?.limits?.maxCoaches;
                const used = data?.usage?.coaches ?? 0;
                setCoachLicenseAtLimit(max != null && max >= 0 && used >= max);
            })
            .catch(() => setCoachLicenseAtLimit(false));
    }, []);

    const loadCoaches = () => {
        setLoading(true);
        API.get('/admin/users', { params: { role: 'COACH' } })
            .then(response => {
                setCoaches(response.data);
                setLoadError(false);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching coaches:', error);
                setCoaches([]);
                setLoadError(true);
                setLoading(false);
            });
        getAdminLicense()
            .then((data) => {
                const max = data?.limits?.maxCoaches;
                const used = data?.usage?.coaches ?? 0;
                setCoachLicenseAtLimit(max != null && max >= 0 && used >= max);
            })
            .catch(() => {});
    };

    useEffect(() => {
        loadCoaches();
    }, []);

    const filteredCoaches = useMemo(() => {
        let result = coaches || [];

        // Apply filter tabs
        if (filter === 'active') {
            result = result.filter(c => c.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(c => c.active === false);
        }
        // 'all' shows everything

        // Apply search query
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => (x ?? '').toString().toLowerCase();
            result = result.filter(coach =>
                [
                    coach.firstName, coach.middleName, coach.lastName,
                    coach.username || coach.userName,
                    coach.email,
                    coach.phoneNumber || coach.phone,
                    coach.id || coach.userId || coach.user_id
                ].some(v => pick(v).includes(term))
            );
        }

        return result;
    }, [coaches, dq, filter]);

    const pagination = useSortedClientPagination(filteredCoaches, {
        columns: userListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const shown = filteredCoaches.length;
    const total = filter === 'all' ? coaches.length : filter === 'active' ? coaches.filter(c => c.active !== false).length : coaches.filter(c => c.active === false).length;

    const coachFilterTabs = [
        { key: 'all', label: t('common.all'), count: coaches.length },
        { key: 'active', label: t('common.active'), count: coaches.filter(c => c.active !== false).length },
        { key: 'archived', label: t('common.archived'), count: coaches.filter(c => c.active === false).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.coaches.title')}
                subtitle={t('pages.coaches.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getUserListExportColumns(t)}
                            data={filteredCoaches}
                            filename="coaches"
                            disabled={filteredCoaches.length === 0}
                        />
                        <button
                            type="button"
                            onClick={() => navigate('/admin/coaches/new')}
                            className="btn-page-action-primary"
                        >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('pages.coaches.add')}
                    </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={coachFilterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.coaches.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.coaches.entityLabel')}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? null : filteredCoaches.length === 0 ? (
                <ListEmptyState
                    message={q ? t('common.noMatchSearch', { entity: t('pages.coaches.entityLabel') }) : t('common.noFound', { entity: t('pages.coaches.entityLabel') }) }
                    hint={q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('pages.coaches.colCoachName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map(coach => (
                                    <tr key={String(coach.id ?? coach.user_id)} className="table-row">
                                        <td className="table-cell whitespace-nowrap">
                                            <ListRecordIdBadge id={resolveRowId(coach)} />
                                        </td>
                                        <td className="table-cell">
                                            {formatFullName(coach.firstName, coach.middleName, coach.lastName) ? (
                                                <UserLink
                                                    id={coach.id}
                                                    name={formatFullName(coach.firstName, coach.middleName, coach.lastName)}
                                                />
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="table-cell">{coach.username || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{coach.email || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{coach.phoneNumber || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={coach.active} />
                                        </td>
                                        <td className="table-cell whitespace-nowrap">
                                            <UserRowActions
                                                user={coach}
                                                entityLabel="coach"
                                                editPath={`/admin/coaches/${coach.id}`}
                                                onStatusChange={loadCoaches}
                                                licenseAtLimit={coachLicenseAtLimit}
                                                extraActions={(
                                                    <button
                                                        type="button"
                                                        onClick={() => setManageModal({
                                                            isOpen: true,
                                                            coachId: coach.id,
                                                            coachName: formatFullName(coach.firstName, coach.middleName, coach.lastName),
                                                        })}
                                                        className="btn-action-view"
                                                        title={t('pages.coaches.manageStudentsTitle')}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                                        </svg>
                                                        {t('pages.coaches.manageStudents')}
                                                    </button>
                                                )}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}

            <ManageStudentAssignmentsModal
                coachId={manageModal.coachId}
                coachName={manageModal.coachName}
                isOpen={manageModal.isOpen}
                onClose={() => setManageModal({ isOpen: false, coachId: null, coachName: '' })}
            />
        </ListPageLayout>
    );
};

export default CoachList;
